import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchAllStudentVacationsAsync,
  deleteStudentVacationAsync,
  clearError,
} from '../store/slices/studentsSlice';
import type { StudentVacationRow } from '../store/slices/studentsSlice';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';

const StudentVacations: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { allStudentVacations, loadingAllVacations, error } = useAppSelector((state) => state.students);

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    row: StudentVacationRow | null;
  }>({ isOpen: false, row: null });

  useEffect(() => {
    dispatch(fetchAllStudentVacationsAsync());
  }, [dispatch]);

  const studentName = (v: StudentVacationRow) => {
    const s = v.student;
    if (!s) return '—';
    return `${s.firstName} ${s.lastName}${s.surname ? ` ${s.surname}` : ''}`;
  };

  const handleDeleteClick = useCallback((row: StudentVacationRow) => {
    setDeleteModal({ isOpen: true, row });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteModal.row?.studentId) return;
    try {
      await dispatch(
        deleteStudentVacationAsync({
          studentId: deleteModal.row.studentId,
          vacationId: deleteModal.row.id,
        })
      ).unwrap();
    } catch {
      // slice error state
    }
    setDeleteModal({ isOpen: false, row: null });
  }, [dispatch, deleteModal.row]);

  const cancelDelete = useCallback(() => {
    setDeleteModal({ isOpen: false, row: null });
  }, []);

  if (loadingAllVacations && allStudentVacations.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Student Vacations</h1>
          <p>Manage leave and absence periods for students in your groups</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/student-vacations/create')}
          className="btn-primary"
        >
          + Add Vacation
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button type="button" onClick={() => dispatch(clearError())} className="close-error">
            ×
          </button>
        </div>
      )}

      {allStudentVacations.length === 0 ? (
        <p style={{ color: '#6b7280', padding: '0 1rem' }}>No student vacation records yet.</p>
      ) : (
        <div className="students-table-scroll" style={{ marginTop: '0.5rem' }}>
          <table className="students-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allStudentVacations.map((v) => (
                <tr key={v.id}>
                  <td>
                    <strong>{studentName(v)}</strong>
                  </td>
                  <td>{v.vacationType?.name || '—'}</td>
                  <td>{formatDateEuropean(v.dateFrom)}</td>
                  <td>{formatDateEuropean(v.dateTo)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/student-vacations/edit/${v.studentId}/${v.id}`)
                        }
                        className="btn-icon"
                        data-tooltip="Edit"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(v)}
                        className="btn-icon danger"
                        data-tooltip="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loadingAllVacations && allStudentVacations.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Delete vacation"
        message="Are you sure you want to delete this student vacation?"
        confirmText="Delete"
        cancelText="Cancel"
        dangerous
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default StudentVacations;
